import { TranslateService } from '@ngx-translate/core';
import { inject, Injectable, OnInit } from '@angular/core';
import { forkJoin, of, Observable, catchError, combineLatest, filter } from 'rxjs';
import { first, map, switchMap, tap, mergeMap } from 'rxjs';
import {
  ListLayout,
  ListLayoutSingleComponent,
  ListLayoutMultipleComponent,
  SimpleConfigBaseFilter,
  ConfigBaseFilter,
  ListSortOption
} from 'gung-list';
import {
  Product,
  ProductListConfigService,
  ProductService,
  AuthService,
  SelectedCustomerService,
  ProductSortService,
  PriceService,
  MetadataService,
  AssortmentService,
  GungFlowService,
  Customer,
  CloudPimFiltersService,
  CurrentAssortmentService,
  Assortment,
  gungComparatorHelper
} from 'gung-standard';
import { ProductItemGroupFilterService } from 'gung-standard-jeeves';
import { ProductCategoryFilterService } from './filters/product-category-filter.service';
import { ProductMerchandiseTypeFilterService } from './filters/product-merchandise-type-filter.service';
import { ProductDepthFilterService } from './filters/product-depth-filter.service';
import { ProductEtiquetteFilterService } from './filters/product-etiquette-filter.service';
import { ProductGondolaBrandFilterService } from './filters/product-gondola-brand-filter.service';
import { ProductLengthFilterService } from './filters/product-length-filter.service';
import { ProductCapacityFilterService } from './filters/product-capacity-filter.service';
import { ProductColorFilterService } from './filters/product-color-filter.service';
import { ProductShelfManufacturerFilterService } from './filters/product-shelf-manufacturer-filter.service';

import { environment } from '../../environments/environment';
import { ProductMousquetairesFilterService } from './filters/product-mousquetaires-filter.service';
import { ProductAssortmentTypeFilterService } from './filters/product-assortment-type-filter.service';
import { ProductPimFilterService } from './filters/product-pim-filter.service';
import { HlDisplayHelperService } from './hl-display-helper.service';
import { ProductExternalCategoryFilterService } from './filters/product-external-category-filter.service';
import { ProductExternalSubCategoryFilterService } from './filters/product-external-sub-category-filter.service';
import { ProductMainFunctionFilterService } from './filters/product-main-function-filter.service';
import { ProductStoreCategoryFilterService } from './filters/product-store-category-filter.service';
import { HlProductService } from './hl-product.service';
import { HlDisplayOrderService } from './hl-display-order.service';
import { ProductHeightFilterService } from './filters/product-height-filter.service';
import { ProductProductShelfTypeFilterService } from './filters/product-product-shelf-type-filter.service';
import { ProductWidthFilterService } from './filters/product-width-filter.service';
import { HlBackendInterceptorService } from './hl-backend-interceptor/hl-backend-interceptor.service';
import {
  FilterSettingsGroup,
  HlDisplayPortalFeaturesService,
  LanguageSettingsGroup,
  PortalFeatures,
  ProductListInfoSettingsGroup
} from './hl-display-portal-features.service';
import { HlProductCardListGridViewComponent } from '../components/hl-product-card-list-grid-view/hl-product-card-list-grid-view.component';
import { HlProductCardListComponent } from '../components/hl-product-card-list/hl-product-card-list.component';
import { HlProductCompressedListViewComponent } from '../components/hl-product-compressed-list-view/hl-product-compressed-list-view.component';
import { PriceSettingsGroup } from './hl-display-portal-features.service';
import { gungGetPropertyOfObject } from 'gung-common';
import { HlDisplayTemplatePortalService } from './hl-display-template-portal.service';
import { HttpClient } from '@angular/common/http';

export class ProductMetadataPimFilterService extends SimpleConfigBaseFilter<Product> {
  constructor(
    protected metadataService: MetadataService,
    protected metadataTable: string,
    protected language: string,
    protected filterLabel: string,
    protected segment: string,
    protected field: string,
    protected isList = false,
    protected isSimpleObjectArray?: boolean
  ) {
    super();
  }

  getName(): string {
    return this.filterLabel;
  }

  /*
  new ProductMetadataPimFilterService(
            this.metaDataService,
            'StoreCategoryNonFood',
            this.translateService.currentLang,
            'STORE_AREA',
            'hlPimItem',
            'ItemStoreCategoryNonFood',
            true
          )
  */
  getOptionIds(item: Product): string[] {
    let options = [];
    if (this.isList) {
      // Here we need to take into consideration if the field values are mapped to language codes or not
      // The difference is if the field is translateable in Struct or not. - Adam

      if (item.extra[this.segment][this.field]) {
        //With the Struct PIM implementation a lot of their previous raw string-array values (from inRiver) are now
        //objects with an Id and (possibly) a Value. We need to handle this differently.
        // This gives us the possibility to only target these new Id-Value objects. Other filters will still work the same as before.
        // - Alexander
        if (this.isSimpleObjectArray) {
          options = item.extra[this.segment][this.field].map(arrayEntry => arrayEntry.Id) || [];
        } else if (typeof item.extra[this.segment][this.field][0] === 'string') {
          options = item.extra[this.segment][this.field] || [];
        } else if (typeof item.extra[this.segment][this.field][0] === 'object') {
          options = item.extra[this.segment][this.field].map(arrayEntry => arrayEntry[this.language]) || [];
        }
      }
    } else {
      options = [item.extra[this.segment][this.field] || ''];
    }
    return options;
  }

  getOptionName(key: string): string {
    const i18nValues = this.metadataService.getMetadataValue(this.metadataTable, 'Values', key);

    let language = this.language;
    if (language === 'en') {
      language = 'en-GB';
    } else if (language === 'se') {
      language = 'sv';
    }

    if (i18nValues?.[language]) {
      return i18nValues?.[language];
    }

    return key;
  }
}

export class ProductItemCustomerChainFilter extends SimpleConfigBaseFilter<Product> {
  constructor() {
    super();
  }

  getName(): string {
    return 'CUSTOMER_CHAIN';
  }

  getOptionIds(item: Product): string[] {
    return [...(item.extra.hlPimItem?.ItemCustomerChain?.map(c => c.Id) || [])];
  }

  getOptionName(key: string): string {
    return key;
  }
}

export class HlProductPathFilter extends SimpleConfigBaseFilter<Product> {
  paths: string[];
  constructor(
    protected filterName: string,
    protected path: string,
    protected booleanFilter: boolean = false
  ) {
    super();
  }

  getName(): string {
    return this.filterName;
  }

  getOptionIds(item: Product): string[] {
    let value = gungGetPropertyOfObject(this.path, item);

    if (!value) {
      return [];
    }
    return [value];
  }

  getOptionName(key: string): string {
    if (this.booleanFilter) {
      if (key === 'true') {
        return 'YES';
      } else if (key === 'false') {
        return 'NO';
      }
      return key;
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class HlDisplayProductListConfigService extends ProductListConfigService {
  protected hldisplayTemplatePortalService = inject(HlDisplayTemplatePortalService);
  protected httpClient = inject(HttpClient);

  private warehouseProductsCache: { [productId: string]: Product };

  currentLang = this.translateService.currentLang;
  defaultLang = null;
  selectedCustomer: Customer;

  roles = [];

  categoryOrderedValues: { [categoryId: string]: number } = {};

  constructor(
    public productService: HlProductService,
    protected productCategoryFilter: ProductCategoryFilterService,
    protected productMerchandiseTypeFilter: ProductMerchandiseTypeFilterService,
    protected productLengthFilter: ProductLengthFilterService,
    protected productDepthFilter: ProductDepthFilterService,
    protected productGondolaBrandFilter: ProductGondolaBrandFilterService,
    protected productEtiquetteFilter: ProductEtiquetteFilterService,
    protected productCapacityFilter: ProductCapacityFilterService,
    protected productColorFilter: ProductColorFilterService,
    protected productShelfManufacturerFilter: ProductShelfManufacturerFilterService,
    protected productItemGroupFilter: ProductItemGroupFilterService,
    protected productMousquetairesFilter: ProductMousquetairesFilterService,
    protected productAssortmentTypeFilter: ProductAssortmentTypeFilterService,
    protected metaDataService: MetadataService,
    protected authService: AuthService,
    protected selectedCustomerService: SelectedCustomerService,
    protected translateService: TranslateService,
    productSortService: ProductSortService,
    protected priceService: PriceService,
    public assortmentService: AssortmentService,
    protected gungFlowService: GungFlowService,
    private helperService: HlDisplayHelperService,
    protected productCategoryFilterService: ProductCategoryFilterService,
    protected productExternalCategoryFilterService: ProductExternalCategoryFilterService,
    protected productExternalSubCategoryFilterService: ProductExternalSubCategoryFilterService,
    protected productMainFunctionFilterService: ProductMainFunctionFilterService,
    protected productStoreCategoryFilterService: ProductStoreCategoryFilterService,
    protected hlDisplayOrderService: HlDisplayOrderService,
    protected productProductShelfTypeFilterService: ProductProductShelfTypeFilterService,
    protected productWidthFilterService: ProductWidthFilterService,
    protected cloudPimFiltersService: CloudPimFiltersService,
    protected currentAssortmentService: CurrentAssortmentService,
    protected hlDisplayPortalFeaturesService: HlDisplayPortalFeaturesService
  ) {
    super(
      productService,
      authService,
      productSortService,
      gungFlowService,
      cloudPimFiltersService,
      currentAssortmentService
    );

    this.authService.getRoles().subscribe(roles => {
      this.roles = roles;
    });
    this.selectedCustomerService
      .getSelectedCustomer()
      .pipe(first())
      .subscribe(selectedCustomer => {
        this.selectedCustomer = selectedCustomer;
      });

    this.hldisplayTemplatePortalService.initSelectedTemplateAssortment();
  }

  getFilters(): ConfigBaseFilter<Product>[] {
    let filters = [];
    this.hlDisplayPortalFeaturesService.getPortalFeatureGroup('filterSettingsGroup').subscribe(filterSettingsGroup => {
      let filterGroup = filterSettingsGroup as FilterSettingsGroup;

      if (filterGroup.hasProductAssortmentTypeFilterEnabled) {
        filters.push(this.productAssortmentTypeFilter);
      }

      if (filterGroup.hasProductMerchandiseTypeFilterEnabled) {
        filters.push(
          new ProductMetadataPimFilterService(
            this.metaDataService,
            'MerchandiseType',
            this.translateService.currentLang,
            'MERCHANDISE_TYPE',
            'hlPimItem',
            'ItemMerchandiseType',
            true,
            true
          )
        );
      }
      if (filterGroup.hasProductSeriesFilterEnabled) {
        filters.push(
          new ProductMetadataPimFilterService(
            this.metaDataService,
            'ProductSeries',
            this.translateService.currentLang,
            'PRODUCT_SERIES',
            'hlPimItem',
            'ItemProductSeries',
            true,
            true
          )
        );
      }

      if (filterGroup.hasProductHeightFilterEnabled) {
        filters.push(new ProductHeightFilterService());
      }
      if (filterGroup.hasProductLengthFilterEnabled) {
        filters.push(this.productLengthFilter);
      }
      if (filterGroup.hasProductDepthFilterEnabled) {
        filters.push(this.productDepthFilter);
      }
      if (filterGroup.hasProductBeenPurchasedByCustomerFilterEnabled) {
        filters.push(new CustomerPurchasedProductFilter());
      }

      if (filterGroup.hasProductRollOutFilterEnabled) {
        filters.push(new RollOutProductFilter());
      }

      if (filterGroup.hasProductShelfTypeFilterEnabled) {
        filters.push(this.productProductShelfTypeFilterService);
      }
      if (filterGroup.hasProductEtiquetteFilterEnabled) {
        filters.push(this.productEtiquetteFilter);
      }

      if (filterGroup.hasProductCategoryFilterEnabled) {
        filters.push(this.productCategoryFilter);
      }
      if (filterGroup.hasProductCapacityFilterEnabled) {
        filters.push(this.productCapacityFilter);
      }
      if (filterGroup.hasProductMousquetairesFilterEnabled) {
        filters.push(this.productMousquetairesFilter);
      }
      if (filterGroup.hasProductDiscountedItemsFilterEnabled) {
        filters.push(new ProductDiscountedItemsFilter());
      }
      if (filterGroup.hasProductExternalSubCategoryFilterEnabled) {
        filters.push(this.productExternalSubCategoryFilterService);
      }
      if (filterGroup.hasProductItemGroupFilterEnabled) {
        filters.push(this.productItemGroupFilter);
      }
      if (filterGroup.hasProductMainFunctionFilterEnabled) {
        filters.push(this.productMainFunctionFilterService);
      }
      if (filterGroup.hasProductStoreCategoryFilterEnabled) {
        filters.push(this.productStoreCategoryFilterService);
      }

      // Metadata Filters
      if (filterGroup.hasProductColorFilterEnabled) {
        filters.push(
          new ProductMetadataPimFilterService(
            this.metaDataService,
            'ColourName',
            this.translateService.currentLang,
            'COLOUR',
            'hlPimItem',
            'ItemColourName',
            true,
            true
          )
        );
      }
      if (filterGroup.hasProductMaterialFilterEnabled) {
        filters.push(
          new ProductMetadataPimFilterService(
            this.metaDataService,
            'Material',
            this.translateService.currentLang,
            'MATERIAL',
            'hlPimItem',
            'ProductStdMaterial',
            true,
            true
          )
        );
      }
      if (filterGroup.hasProductShelfManufacturerFilterEnabled) {
        filters.push(
          new ProductMetadataPimFilterService(
            this.metaDataService,
            'ShelfManufacturer',
            this.translateService.currentLang,
            'SHELF_MANUFACTURER',
            'hlPimProduct',
            'ProductShelfManufacturer',
            true
          )
        );
      }

      if (filterGroup.hasProductStoreNonFoodFilterEnabled) {
        filters.push(
          new ProductMetadataPimFilterService(
            this.metaDataService,
            'StoreCategoryNonFood',
            this.translateService.currentLang,
            'STORE_AREA',
            'hlPimItem',
            'ItemStoreCategoryNonFood',
            true,
            true
          )
        );
      }

      if (filterGroup.hasProductMerchandiseBrandFilterEnabled) {
        filters.push(
          new ProductMetadataPimFilterService(
            this.metaDataService,
            'MerchandiseBrand',
            this.translateService.currentLang,
            'MERCHANDISE_BRAND',
            'hlPimItem',
            'ItemMerchandiseBrand',
            true,
            true
          )
        );
      }

      // Inriver Filters
      if (filterGroup.hasProductWidthFilterEnabled) {
        filters.push(new ProductPimFilterService('WIDTH_MM', 'hlPimItem', 'ItemWidthmm', ' mm'));
      }
      if (filterGroup.hasProductSizeFilterEnabled) {
        //essentially a copy of capacity filter - but with another name?
        //This is used for e.g fr-portals, whilst capacity is used on e.g se
        filters.push(new ProductPimFilterService('SIZE', 'hlPimItem', 'ItemSize'));
      }
      if (filterGroup.hasProductItemDiameterMmEnabled) {
        filters.push(new ProductPimFilterService('DIAMETER_MM', 'hlPimItem', 'ItemDiametermm', ' mm'));
      }

      /*
      DUPLICATE FILTER
      if (filterGroup.hasProductStoreDepartmentFilterEnabled) {
        filters.push(new ProductPimFilterService('STORE_DEPARTMENT', 'hlPimProduct', 'ProductStoreCategory', '', true));
      }*/
      if (filterGroup.hasProductTrademarkFilterEnabled) {
        filters.push(new ProductPimFilterService('TRADEMARK', 'hlPimProduct', 'ProductRange', '', true));
      }
      if (filterGroup.hasProductSystemEnabled) {
        filters.push(new ProductPimFilterService('PRODUCT_SYSTEM', 'hlPimProduct', 'ProductSystem'));
      }
      if (filterGroup.hasProductFrontFilterEnabled) {
        filters.push(new ProductPimFilterService('FRONT', 'hlPimItem', 'ItemFront'));
      }
      if (filterGroup.hasProductLabelTypeFilterEnabled) {
        filters.push(new ProductPimFilterService('LABEL_TYPE', 'hlPimItem', 'LabelType'));
      }
      if (filterGroup.hasProductSpringFilterEnabled) {
        filters.push(new ProductPimFilterService('SPRING', 'hlPimItem', 'ItemSpring'));
      }

      if (filterGroup.hasProductItemCustomerChainFilterEnabled) {
        filters.push(new ProductItemCustomerChainFilter());
      }
      if (filterGroup.hasProductItemSparePartFilterEnabled) {
        filters.push(new HlProductPathFilter('SPARE_PARTS', `extra.hlPimItem.ItemSparePart`, true));
      }
      if (filterGroup.hasProductItemTopSignFilterEnabled) {
        filters.push(new HlProductPathFilter('ITEM_TOP_SIGN', `extra.i18n.${this.currentLang}.ItemTopSign_Value`));
      }
      if (filterGroup.hasProductItemPrintIncludedFilterEnabled) {
        filters.push(
          new HlProductPathFilter('ITEM_PRINT_INCLUDED', `extra.i18n.${this.currentLang}.ItemPrintIncluded_Value`)
        );
      }
      if (filterGroup.hasProductItemScoopHolderFilterEnabled) {
        filters.push(
          new HlProductPathFilter('ITEM_SCOOP_HOLDER', `extra.i18n.${this.currentLang}.ItemScoopHolder_Value`)
        );
      }
      if (filterGroup.hasProductItemBagHolderFilterEnabled) {
        filters.push(new HlProductPathFilter('ITEM_BAG_HOLDER', `extra.i18n.${this.currentLang}.ItemBagHolder_Value`));
      }
    });

    return filters;
  }

  getWarehouseProducts(): Observable<Product[]> {
    return this.productService.getProductsByAssortment('warehouse');
  }

  getItems(): Observable<Product[]> {
    return this.getInternalItems().pipe(
      switchMap(products =>
        forkJoin({
          products: of(products),
          orderedValueData: this.selectedCustomerService.getSelectedCustomer().pipe(
            first(),
            switchMap(customer => {
              if (customer.extra?.kus?.q_financialgrpcode && customer.extra?.kus?.q_salesmarket_code) {
                return this.httpClient.get<{ [productId: string]: number }>(
                  `json/hl-display/ordered-value-data?financialGroup=${customer.extra.kus.q_financialgrpcode}&salesMarketCode=${customer.extra.kus.q_salesmarket_code}`
                ).pipe(catchError(() => of({})));
              } else {
                return of({});
              }
            })
          )
        })
      ),
      switchMap(({ products, orderedValueData }) => {
        for (const product of products) {
          product.extra._orderedValue = orderedValueData[product.id] ?? 0;
          const category = product.extra.i18n?.[this.translateService.currentLang || 'se'].ProductExternalCategory;
          this.categoryOrderedValues[category] = (this.categoryOrderedValues[category] ?? 0) + product.extra._orderedValue;
        }
        return of(products);
      })
    );
  }

  protected getInternalItems(): Observable<Product[]> {
    // Combine all required initial data in a single stream
    return combineLatest({
      customer: this.selectedCustomerService.getSelectedCustomer().pipe(first()),
      filterSettings: this.hlDisplayPortalFeaturesService
        .getPortalFeatureGroup('filterSettingsGroup')
        .pipe(map(group => group as FilterSettingsGroup)),
      languageSettings: this.hlDisplayPortalFeaturesService
        .getPortalFeatureGroup('languageSettingsGroup')
        .pipe(map(group => group as LanguageSettingsGroup)),
      priceSettings: this.hlDisplayPortalFeaturesService
        .getPortalFeatureGroup('priceSettingsGroup')
        .pipe(map(group => group as PriceSettingsGroup)),
      userRoles: this.authService.getRoles().pipe(first()),
      productListInfoSettings: this.hlDisplayPortalFeaturesService
        .getPortalFeatureGroup('productListInfoSettingsGroup')
        .pipe(map(group => group as ProductListInfoSettingsGroup)),
      selectedTemplateAssortmentId: this.hldisplayTemplatePortalService.getSelectedTemplateAssortmentId()
    }).pipe(
      tap(({ languageSettings, customer }) => {
        // Store any needed values as side effects
        this.defaultLang = languageSettings.defaultLanguage;
        this.selectedCustomer = customer;
      }),
      // Switch to the appropriate product retrieval strategy based on user roles
      switchMap(
        ({
          customer,
          filterSettings,
          priceSettings,
          userRoles,
          productListInfoSettings,
          selectedTemplateAssortmentId
        }) => {
          // Determine which retrieval strategy to use
          if (userRoles.includes('SALES')) {
            return this.getSalesRoleProducts(customer, filterSettings, priceSettings);
          } else if (userRoles.includes('MANAGER') && productListInfoSettings.templatePortal) {
            return this.getManagerTemplateProducts(customer, selectedTemplateAssortmentId);
          } else if (filterSettings.hasProductBeenPurchasedByCustomerFilterEnabled) {
            return this.getProductsWithPurchaseHistory(customer, filterSettings);
          } else {
            return this.getStandardUserProducts(customer, filterSettings);
          }
        }
      )
    );
  }

  protected getTemplatePortalProducts(selectedTemplateAssortmentId: string): Observable<Product[]> {
    return this.gungFlowService.getSelectedFlow().pipe(
      filter(selectedFlow => !!selectedFlow),
      switchMap(selectedFlow =>
        this.productService.getProductsByAssortmentAndFlow(selectedTemplateAssortmentId, selectedFlow.id).pipe(first())
      ),
      switchMap(products =>
        forkJoin({
          products: of(products),
          productAssorment: this.assortmentService.getAssortment(selectedTemplateAssortmentId).pipe(first())
        })
      ),
      switchMap(({ products, productAssorment }) => {
        for (const product of products) {
          const assortmentChild = productAssorment.children.find(a => a.id === product.id);
          product.extra.templatelimitlowant = assortmentChild.extra.limitlowant || 1;
        }

        return of(products);
      })
    );
  }

  protected getManagerTemplateProducts(
    customer: Customer,
    selectedTemplateAssortmentId?: string
  ): Observable<Product[]> {
    return this.helperService
      .checkRequestPricelistAssortmentWithWebCondition(customer.extra.kus.prislista)
      .pipe(first(), switchMap(fullAssortmentProductListId => forkJoin({
      products: this.getTemplatePortalProducts(selectedTemplateAssortmentId).pipe(first()),
      priceListAssortment: this.assortmentService.getAssortment(selectedTemplateAssortmentId).pipe(first()),
      productsFullAssortment: this.getTemplatePortalProducts(fullAssortmentProductListId).pipe(
        first(),
        catchError(() => of([]))
      ),
      priceListAssortmentFullAssortment: this.assortmentService.getAssortment(fullAssortmentProductListId).pipe(
        first(),
        catchError(() => of({ children: [] }))
      )
    }).pipe(
      // Apply purchase history markers in one operation
      map(({ products, priceListAssortment, productsFullAssortment, priceListAssortmentFullAssortment }) => {
        // Create product lookup sets for efficiency
        const customerPriceListId = '' + customer.extra.kus.prislista;

        // Create assortment type lookup sets
        const customerProductIds = new Set([
          ...priceListAssortment.children
            .filter(child => child.extra.pricelist === customerPriceListId)
            .map(child => child.id),
          ...(priceListAssortmentFullAssortment?.children
            ?.filter(child => child.extra.pricelist === customerPriceListId)
            .map(child => child.id) || [])
        ]);

        const templateProductIds = new Set([
          ...priceListAssortment.children
            .filter(child => child.extra.pricelist !== customerPriceListId)
            .map(child => child.id),
          ...(priceListAssortmentFullAssortment?.children
            ?.filter(child => child.extra.pricelist !== customerPriceListId)
            .map(child => child.id) || [])
        ]);

        // Merge products and filter out duplicates
        const productMap = new Map<string, Product>();

        // Add products from the first array
        products.forEach(product => {
          productMap.set(product.id, product);
        });

        // Add products from the full assortment array (only if not already present)
        productsFullAssortment.forEach(product => {
          if (!productMap.has(product.id)) {
            productMap.set(product.id, product);
          }
        });

        // Convert map back to array
        const processedProducts = Array.from(productMap.values());

        // Assign metadata in a single pass
        processedProducts.forEach(product => {
          // Assign assortment type
          if (templateProductIds.has(product.id) && customerProductIds.has(product.id)) {
            product.extra.assortmentType = 'T,C';
          } else if (templateProductIds.has(product.id)) {
            product.extra.assortmentType = 'T';
          } else if (customerProductIds.has(product.id)) {
            product.extra.assortmentType = 'C';
          } else {
            product.extra.assortmentType = 'W';
          }
        });

        // Sort once
        return processedProducts.sort((p1, p2) => {
          const a = p1.extra.ar.artbeskr || 'ZZZZZZZZZZZZZZZZZ';
          const b = p2.extra.ar.artbeskr || 'ZZZZZZZZZZZZZZZZZ';
          return a.localeCompare(b);
        });
      }),
      catchError(error => {
        return this.getTemplatePortalProducts(selectedTemplateAssortmentId).pipe(first());
      })
    )));
  }

  private getSalesRoleProducts(
    customer: Customer,
    filterSettings: FilterSettingsGroup,
    priceSettings: PriceSettingsGroup
  ): Observable<Product[]> {
    return this.helperService
      .checkRequestPricelistAssortmentWithWebCondition(customer.extra.kus.prislista)
      .pipe(
        first(),
        switchMap(pricelistId => {
          if (!pricelistId) {
            return this.getWarehouseProducts().pipe(
              first(),
              map(warehouseProducts => {
                warehouseProducts.forEach(product => {
                  product.extra.assortmentType = 'W';
                });
                return warehouseProducts.sort((p1, p2) => {
                  const a = p1.extra.ar.artbeskr || 'ZZZZZZZZZZZZZZZZZ';
                  const b = p2.extra.ar.artbeskr || 'ZZZZZZZZZZZZZZZZZ';
                  return a.localeCompare(b);
                });
              })
            );
          }
          return forkJoin({
      products: this.productService.getProductsByAssortment(pricelistId).pipe(first()),
      priceListAssortment: this.assortmentService.getAssortment(pricelistId).pipe(first()),
      warehouseProducts: priceSettings.includeWarehouseProductsInProductList
        ? this.getWarehouseProducts().pipe(first())
        : of([]),
      orderRows: filterSettings.hasProductBeenPurchasedByCustomerFilterEnabled
        ? this.hlDisplayOrderService.getOrderRows(customer.id).pipe(first())
        : of([])
    }).pipe(
      // Apply purchase history markers in one operation
      map(({ products, priceListAssortment, warehouseProducts, orderRows }) => {
        // Mark purchased products
        const purchasedIds = new Set(orderRows.map(row => row.productId));

        // Create product lookup sets for efficiency
        const productIdSet = new Set(products.map(p => p.id));
        const customerPriceListId = '' + customer.extra.kus.prislista;

        // Create assortment type lookup sets
        const customerProductIds = new Set(
          priceListAssortment.children
            .filter(child => child.extra.pricelist === customerPriceListId)
            .map(child => child.id)
        );

        const standardProductIds = new Set(
          priceListAssortment.children
            .filter(child => child.extra.pricelist !== customerPriceListId)
            .map(child => child.id)
        );

        // Process all products in a single pass
        const processedProducts = [...products];

        // Add unique warehouse products
        warehouseProducts.forEach(product => {
          if (!productIdSet.has(product.id)) {
            processedProducts.push(product);
          }
        });

        // Assign metadata in a single pass
        processedProducts.forEach(product => {
          // Mark purchased products
          if (purchasedIds.has(product.id)) {
            product.extra._customerPurchasedProduct = true;
          }

          // Assign assortment type
          if (customerProductIds.has(product.id)) {
            product.extra.assortmentType = 'C';
          } else if (standardProductIds.has(product.id)) {
            product.extra.assortmentType = 'S';
          } else {
            product.extra.assortmentType = 'W';
          }
        });

        // Sort once
        return processedProducts.sort((p1, p2) => {
          const a = p1.extra.ar.artbeskr || 'ZZZZZZZZZZZZZZZZZ';
          const b = p2.extra.ar.artbeskr || 'ZZZZZZZZZZZZZZZZZ';
          return a.localeCompare(b);
        });
      }),
      // Handle any errors in the main flow
      catchError(error => {
        console.error('Error fetching product data:', error);

        // Fallback to warehouse products on error
        return this.getWarehouseProducts().pipe(
          first(),
          map(warehouseProducts => {
            warehouseProducts.forEach(product => {
              product.extra.assortmentType = 'W';
            });

            return warehouseProducts.sort((p1, p2) => {
              const a = p1.extra.ar.artbeskr || 'ZZZZZZZZZZZZZZZZZ';
              const b = p2.extra.ar.artbeskr || 'ZZZZZZZZZZZZZZZZZ';
              return a.localeCompare(b);
            });
          })
        );
      })
    );
    })
  );
  }

  private getProductsWithPurchaseHistory(
    customer: Customer,
    filterSettings: FilterSettingsGroup
  ): Observable<Product[]> {
    return this.helperService
      .checkRequestPricelistAssortmentWithWebCondition(customer.extra.kus.prislista)
      .pipe(first(), switchMap(pricelistId => this.productService.getProductsByAssortment(pricelistId).pipe(
      // Get order history and merge with products
      switchMap(products =>
        this.hlDisplayOrderService.getOrderRows(customer.id).pipe(
          first(),
          map(orderRows => {
            const purchasedIds = new Set(orderRows.map(row => row.productId));

            // Mark purchased products
            products.forEach(product => {
              if (purchasedIds.has(product.id)) {
                product.extra._customerPurchasedProduct = true;
              }
            });

            return products;
          })
        )
      ),
      map(products => {
        // Filter out blocked items
        const blockedItems = this.getBlockedItems();
        const filteredProducts = products.filter(item => !blockedItems.includes(item.id));

        // Add customer-specific product codes if needed
        if (this.roles.includes('MANAGER') && customer?.extra.kuarMapped) {
          filteredProducts.forEach(p => {
            if (customer.extra.kuarMapped?.[p.id]) {
              p.extra._artnrkund = customer.extra.kuarMapped[p.id].artnrkund;
            }
          });
        }

        return filteredProducts.sort((a, b) => a.name.localeCompare(b.name));
      })
    )));
  }

  private getStandardUserProducts(customer: Customer, filterSettings: FilterSettingsGroup): Observable<Product[]> {
    return this.authService.getCurrentUser().pipe(
      switchMap(user => this.productService.getProductsByAssortment(user.assortment).pipe(first())),
      map(products => {
        // Add customer reference numbers if USER role
        if (this.roles.length === 1 && this.roles.includes('USER') && customer?.extra.kuarMapped) {
          products.forEach(p => {
            p.extra._kuar = customer.extra.kuarMapped?.[p.id]?.artnrkund || '';
          });
        }
        return products;
      }),
      // Get order history and merge with products
      switchMap(products =>
        customer
          ? this.hlDisplayOrderService.getOrderRows(customer.id).pipe(
              first(),
              map(orderRows => {
                const purchasedIds = new Set(orderRows.map(row => row.productId));

                // Mark purchased products
                products.forEach(product => {
                  if (purchasedIds.has(product.id)) {
                    product.extra._customerPurchasedProduct = true;
                  }
                });

                return products;
              })
            )
          : of(products)
      ),
      map(products => {
        // Filter out blocked items
        const blockedItems = this.getBlockedItems();
        const filteredProducts = products.filter(item => !blockedItems.includes(item.id));

        return filteredProducts.sort((a, b) => a.name.localeCompare(b.name));
      })
    );
  }

  getBatchSizes(): number[] {
    return [20, 80, 120, 160];
  }

  getLayouts(): ListLayout<Product>[] {
    if (this.roles.includes('CUSTOMER_SERVICE')) {
      return [
        {
          getIconClass: () => 'fa-solid fa-table-list',
          getButtonClass: () => 'd-none d-sm-none d-md-inline-block',
          getListItemComponent: () => HlProductCompressedListViewComponent,
          getListLayoutComponent: () => ListLayoutMultipleComponent,
          getName: () => 'Compressed list view'
        },
        {
          getIconClass: () => 'fa fa-list',
          getListItemComponent: () => HlProductCardListComponent,
          getListLayoutComponent: () => ListLayoutMultipleComponent,
          getName: () => 'Product grid'
        },
        {
          getIconClass: () => 'fas fa-th',
          getListItemComponent: () => HlProductCardListGridViewComponent,
          getListLayoutComponent: () => ListLayoutMultipleComponent,
          getName: () => 'Grid View'
        }
      ];
    }

    if (this.roles.length === 1 && this.roles.includes('USER')) {
      //Go trough: was previously isCustomerPorta && !isNOCustomerPortal
      return [
        {
          getIconClass: () => 'fas fa-th',
          getListItemComponent: () => HlProductCardListGridViewComponent,
          getListLayoutComponent: () => ListLayoutMultipleComponent,
          getName: () => 'Grid View'
        },
        {
          getIconClass: () => 'fa fa-list',
          getListItemComponent: () => HlProductCardListComponent,
          getListLayoutComponent: () => ListLayoutMultipleComponent,
          getName: () => 'Product grid'
        },
        {
          getIconClass: () => 'fa-solid fa-table-list',
          getButtonClass: () => 'd-none d-sm-none d-md-inline-block',
          getListItemComponent: () => HlProductCompressedListViewComponent,
          getListLayoutComponent: () => ListLayoutMultipleComponent,
          getName: () => 'Compressed list view'
        }
      ];
    }
    return [
      {
        getIconClass: () => 'fa fa-list',
        getListItemComponent: () => HlProductCardListComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'Product grid'
      },
      {
        getIconClass: () => 'fas fa-th',
        getListItemComponent: () => HlProductCardListGridViewComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'Grid View'
      },
      {
        getIconClass: () => 'fa-solid fa-table-list',
        getButtonClass: () => 'd-none d-sm-none d-md-inline-block',
        getListItemComponent: () => HlProductCompressedListViewComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'Compressed list view'
      }
    ];
  }

  getSearchTerms(item: Product): string[] {
    let result = [];
    if (item.id) {
      result.push(item.id);
    }
    if (item.name) {
      result.push(item.name);
    }

    if (item.description) {
      result.push(item.description);
    }

    if (item?.extra?.i18n?.[this.currentLang]?.artbeskr2) {
      result.push(item.extra.i18n[this.currentLang].artbeskr2);
    }

    result = [...result, ...this.getTranslatedTerms(item)];

    const name = this.getName(item);
    if (name) {
      result.push(name);
    }

    if (item.extra.ar.varugruppkod) {
      const value = this.metaDataService.getMetadataValue('vg', 'varugruppbeskr', item.extra.ar.varugruppkod);
      if (value) {
        result.push(value);
      }
    }

    if (item.extra.hlPimItem.ItemMerchandiseType && item.extra.hlPimItem.ItemMerchandiseType.length > 0) {
      if (typeof item.extra.hlPimItem.ItemMerchandiseType[0] === 'object') {
        const filteredMerchandiseTypes = item.extra.hlPimItem.ItemMerchandiseType.filter(type => !!type);
        result = [...result, ...filteredMerchandiseTypes.map(type => type.Id)];
      } else {
        result = [...result, ...item.extra.hlPimItem.ItemMerchandiseType.filter(type => !!type)];
      }
    }

    if (item.extra._artnrkund) {
      result.push(item.extra._artnrkund);
    }

    if (item.extra._kuar) {
      result.push(item.extra._kuar);
    }

    let itemDesc = item.extra.ar.artbeskr;
    if (item.extra?.i18n?.[this.currentLang]) {
      itemDesc = item.extra.i18n[this.currentLang].artbeskr || itemDesc;
    }

    result.push(itemDesc);

    if (item.extra.arb) {
      result.push(item.extra.arb.artBeskrSpec);
      result.push(item.extra.arb.itemSpecWeb);
    }
    if (item.extra.ar) {
      result.push(item.extra.ar.artbeskrspec);
      result.push(item.extra.ar.artbeskr);
      result.push(item.extra.ar.artnr);
    }
    if (item.extra.kuar) {
      result.push(item.extra.ar.artnrkund);
    }

    let artnrkund = this.selectedCustomer?.extra?.kuarMapped?.[item.id]?.artnrkund;
    if (artnrkund) {
      result.push(artnrkund);
    }

    if (this.roles.includes('CUSTOMER_SERVICE')) {
      if (item.extra?.ar?.q_hl_ext_item_number) {
        result.push(item.extra.ar.q_hl_ext_item_number);
      }
    }

    return result;
  }

  private getTranslatedTerms(item: Product): string[] {
    if (!item.extra.i18n) {
      return [];
    }

    const selectedLanguageCode = this.translateService.currentLang;
    const result = [];

    const i18n = item.extra.i18n;

    [this.defaultLang, selectedLanguageCode].forEach(languageCode => {
      const languageProperties = i18n[languageCode];
      if (!languageProperties) {
        return;
      }

      if (languageProperties.artbeskrspec) {
        result.push(languageProperties.artbeskrspec);
      }
      if (languageProperties.itemspecweb) {
        result.push(languageProperties.itemspecweb);
      }
      if (languageProperties.artbeskr) {
        result.push(languageProperties.artbeskr);
      }
    });

    return result;
  }

  protected getBlockedItems(): string[] {
    // Customer portal
    if (!this.roles.includes('SALES')) {
      /**
       * These product ids are 'blocked', meaning that they should not be viewed in the product list.
       * This is kept as a comment for legacy purposes, to revise to if this should be reimplemented in the future.
       */
      /* if (environment.mainCountry === 'no') {
        return ['527859', '991100', '785710', '534222', '798076', '900000'];
      }
      if (
        environment.mainCountry === 'fr' &&
        (environment.mainCustomer === 'Pharmacies' || environment.mainCustomer === 'General') //Go through: should be replaced by a feature?
      ) {
        return ['527859', '900000'];
      } */
      return ['900000'];
    }
    return [];
  }

  getSearchGroupCss(): string {
    return '';
  }

  getFilterGroupCss(): string {
    return '';
  }

  getName(product: Product): string {
    if (!this.roles.includes('SALES')) {
      //Go through: was previously !environment.sales
      return product.name;
    }

    const id = product.id;

    // const itemGroup = this.metaDataService.getMetadataValue('vg', 'varugruppbeskr', product.extra.ar.varugruppkod);
    const itemProductSystem = product.extra.hlPimProduct?.ProductSystem;
    const itemDesc =
      this.i18nSeExists(product) && product.extra.i18n.se.artbeskr
        ? product.extra.i18n.se.artbeskr
        : product.extra.ar.artbeskr;
    return `${id}${itemProductSystem ? ' - ' + itemProductSystem : ''} - ${itemDesc}`;
  }

  i18nSeExists(data: Product): boolean {
    return data.extra.i18n && data.extra.i18n.se;
  }

  hideZeroOptions() {
    return true;
  }

  protected sortGetName(product: Product): string {
    if (product.extra.i18n[this.translateService.currentLang]?.artbeskrspec) {
      return product.extra.i18n[this.translateService.currentLang].artbeskrspec;
    } else if (product.extra.ar?.artbeskrspec) {
      return product.extra.ar.artbeskrspec;
    } else {
      return product.extra.ar.artbeskr;
    }
  }

  getSortOptions(): ListSortOption<Product>[] {
    const sortOptions: ListSortOption<Product>[] = [
      {
        getLabel: () =>
          this.translateService.instant('ARTICLE_NAME') + ' (' + this.translateService.instant('DESC') + ')',
        getComparator: () => (item1: Product, item2: Product) =>
          gungComparatorHelper(this.sortGetName(item1), this.sortGetName(item2), -1)
      },
      {
        getLabel: () =>
          this.translateService.instant('ARTICLE_NAME') + ' (' + this.translateService.instant('ASC') + ')',
        getComparator: () => (item1: Product, item2: Product) =>
          gungComparatorHelper(this.sortGetName(item1), this.sortGetName(item2), 1)
      },
      {
        getLabel: () => this.translateService.instant('ITEM_ID') + ' (' + this.translateService.instant('DESC') + ')',
        getComparator: () => (item1: Product, item2: Product) => {
          const id1: number | string = !isNaN(Number(item1?.id)) ? item1?.id : Number(item1?.id);
          const id2: number | string = !isNaN(Number(item2?.id)) ? item2?.id : Number(item2?.id);
          return gungComparatorHelper(id1, id2, -1);
        }
      },
      {
        getLabel: () => this.translateService.instant('ITEM_ID') + ' (' + this.translateService.instant('ASC') + ')',
        getComparator: () => (item1: Product, item2: Product) => {
          const id1: number | string = !isNaN(Number(item1?.id)) ? item1?.id : Number(item1?.id);
          const id2: number | string = !isNaN(Number(item2?.id)) ? item2?.id : Number(item2?.id);
          return gungComparatorHelper(id1, id2, 1);
        }
      },
      {
        getLabel: () => 'TOP_PRODUCTS',
        getComparator: () => (item1: Product, item2: Product) =>
          gungComparatorHelper(item1?.extra?._orderedValue || 0, item2?.extra?._orderedValue || 0, -1)
      },
      {
        getLabel: () => 'TOP_PRODUCTS_BY_CATEGORY',
        getComparator: () => (item1: Product, item2: Product) => {
          const category1 = item1.extra.i18n?.[this.translateService.currentLang || 'se']?.ProductExternalCategory;
          const category2 = item2.extra.i18n?.[this.translateService.currentLang || 'se']?.ProductExternalCategory;

          const categoryValue1 = this.categoryOrderedValues[category1] || 0;
          const categoryValue2 = this.categoryOrderedValues[category2] || 0;

          // First sort by category ordered value (descending)
          if (categoryValue1 !== categoryValue2) {
            return gungComparatorHelper(categoryValue1, categoryValue2, -1);
          }

          // If same category, sort by product ordered value (descending)
          return gungComparatorHelper(item1?.extra?._orderedValue || 0, item2?.extra?._orderedValue || 0, -1);
        }
      }
    ];

    if (this.roles.includes('SALES')) {
      sortOptions.push({
        getLabel: () =>
          this.translateService.instant('INTERNAL_NAME') + ' (' + this.translateService.instant('DESC') + ')',
        getComparator: () => (item1: Product, item2: Product) =>
          gungComparatorHelper(item1.extra?.ar?.artbeskr || '', item2.extra?.ar?.artbeskr || '', -1)
      });

      sortOptions.push({
        getLabel: () =>
          this.translateService.instant('INTERNAL_NAME') + ' (' + this.translateService.instant('ASC') + ')',
        getComparator: () => (item1: Product, item2: Product) =>
          gungComparatorHelper(item1.extra?.ar?.artbeskr || '', item2.extra?.ar?.artbeskr || '', 1)
      });
    }

    return sortOptions;
  }
}
function peek(arg0: (products: any) => void): import('rxjs').OperatorFunction<Product[], unknown> {
  throw new Error('Function not implemented.');
}

export class CustomerPurchasedProductFilter extends SimpleConfigBaseFilter<Product> {
  getName(): string {
    return 'CUSTOMER_PURCHASED_PRODUCT';
  }
  getOptionIds(item: Product): string[] {
    if (!!item.extra?._customerPurchasedProduct) {
      return ['CUSTOMER_PURCHASED_PRODUCT_FILTER_SELECTED'];
    }
    return [];
  }
  getOptionName(key: string): string {
    return `${key}`;
  }
}

export class RollOutProductFilter extends SimpleConfigBaseFilter<Product> {
  getName(): string {
    return 'ROLL_OUT';
  }
  getOptionIds(item: Product): string[] {
    if (!!item.extra?.hlPimItem?.ItemCSRollOut) {
      return ['ROLL_OUT'];
    }
    return [];
  }
  getOptionName(key: string): string {
    return `${key}`;
  }
}

export class ProductDiscountedItemsFilter extends SimpleConfigBaseFilter<Product> {
  getName(): string {
    return 'DISCOUNTED_ITEMS';
  }
  getOptionIds(item: Product): string[] {
    if (item?.extra?.price?.backendPrice?.levels?.[0]?.extra?.prl?.proc1 > 0) {
      // This conditions is not correct
      return ['DISCOUNTED_FILTER_SELECTED'];
    }
    return [];
  }
  getOptionName(key: string): string {
    return `${key}`;
  }
}
